import {
  Divider,
  Group,
  LoadingOverlay,
  Select,
  SimpleGrid,
  Skeleton,
  Space,
  Stack,
  // eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useContext, useRef, useState } from 'react';
import { useElementSize } from '@mantine/hooks';
import { DatePicker } from '@mantine/dates';
import { Trans, useLingui } from '@lingui/react';
import { addHours } from 'date-fns';
import { useDrop } from 'react-dnd-latest';
import { useNavigate } from 'react-router-dom';
import { DocumentContext } from '../../Service/Context/DocumentContextV2';
import { ProfileContext } from '../../Service/Context/ProfileContext';
import BookitupSegmentedControl from '../../Atoms/BookitupSegmentedControl';
import { DataS } from '../../Service/DataS';
import { DragItem, PositionDragItem, ShippingType, TaxType } from '../../Service/DocumentT';
import PositionHeader from '../../Molecules/Document/Position/PositionHeader';
import DocumentPosition from '../../Molecules/Document/Position/DocumentPositionV2';
import DropArea from '../../Atoms/DropArea';
import { EventRichInput, RichInput } from '../tiptap/TiptapEditorInput';
import DocumentSum from '../../Molecules/Document/DocumentSum';
import DocumentPartialInvoices from '../../Molecules/Event/Documents/invoices/DocumentPartialInvoices';
import UnassginedPartialInvoices from '../../Molecules/Event/Documents/invoices/UnassignedPartialInvoices';
import PositionGroups from '../../Molecules/Document/Position/PositionGroups';
import SkeletonItems, { EmptyPositionsSkeleton } from '../../Atoms/SkeletonItems';
import { openAddProductModal } from '../../Molecules/Document/Position/AddProductModal';
import { useEventScope } from '../../Service/hooks/useEventScope';
import DocumentBubbleMenu from '../tiptap/toolbars/BubbleMenu';
import { TiptapS } from '../../Service/TiptapS';
import { DocumentS } from '../../Templates/Documents/DocumentS';
import { ThemeText } from '../../Atoms/ThemeText';
import { usePositionProducts } from '../../Service/hooks/usePositionProducts';
import { ViolationContext } from '../../Service/Context/ViolationContext';
import { LicenceS } from '../../Service/LicenceS';
import LinkText from '../../Atoms/LinkText';
import { WindowContext } from '../../Service/Context/WindowContext';
import { useJwtClaims } from '../../Service/Context/AuthContext';

const DocumentSkeleton = () => {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { licence } = useJwtClaims();
  const { ref, width } = useElementSize();
  const positionsSectionRef = useRef<HTMLDivElement>(null);
  const { isEventScope } = useEventScope();
  const { loading, bookitupDocument, positionHandlers, savePatch, groupHandlers, refresh } =
    useContext(DocumentContext);
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const [addingPosition, setAddingPosition] = useState(false);
  const { products } = usePositionProducts();
  const { addPosition, removePositionFromGroup, addProducts } = positionHandlers;
  const { addGroup, addingGroupAtIndex, movingGroup } = groupHandlers;
  const { profile } = useContext(ProfileContext);
  const { sm: isMobile } = useContext(WindowContext);
  const { smallScaleBusiness } = profile ?? {};
  const hasFlowLicence = LicenceS.hasSuitableLicence('FLOW', licence);

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DragItem.POSITION,
      canDrop: (item: PositionDragItem) => item.position.groupId !== null,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
      drop: (item: PositionDragItem) => removePositionFromGroup(item.position.id),
    }),
    [removePositionFromGroup],
  );

  const addProduct = () =>
    executeWithLicenceCheck(LicenceS.Restrictions.PRODUCT_USAGE, () => {
      openAddProductModal({ products, onFinish: (p) => addProducts(p) });
    });

  const addGroupHandler = () => {
    addGroup();
    positionsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const addPositionHandler = async () => {
    setAddingPosition(true);
    try {
      await addPosition();
    } finally {
      setAddingPosition(false);
    }
  };

  const {
    draft,
    taxType,
    documentType,
    positions,
    positionGroups,
    currencySymbol,
    shippingType,
    date,
    validUntil,
    number,
    shippingDate,
    shippingEndDate,
    originDocumentId,
    eventId,
    originalInvoiceNumber,
    originalDocumentType,
  } = bookitupDocument ?? {
    positions: [],
    positionGroups: [],
    currencySymbol: '€',
  };

  const grouplessPositions = positions.filter((position) => position.groupId === null);
  const emptyGroups = positionGroups.length === 0;
  const emptyPositions = grouplessPositions.length === 0;
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';
  const usePreviewVersion = width > 0 && width < 800;
  const hasShippingType = ['CONFIRMATION', 'INVOICE', 'PARTIAL_INVOICE', 'REMINDER'].includes(documentType ?? '');
  const isReminder = documentType === 'REMINDER';
  const hasShippingPeriod = shippingType === 'serviceperiod' || shippingType === 'deliveryperiod';
  const modifyPickedDay = (_date: Date) => addHours(_date, 2);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <LoadingOverlay visible={movingGroup} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <Group position="apart" mb="md">
        <Stack spacing={2}>
          <ThemeText size="lg" weight="bolder">
            {loading && 'Laden ...'}
            {!loading && bookitupDocument && (
              <ThemeText>
                {DocumentS.resolveLabel(bookitupDocument)}
                {draft && (
                  <Text span ml={5} color="dimmed" size="sm" weight="normal">
                    (Entwurf)
                  </Text>
                )}
              </ThemeText>
            )}
          </ThemeText>
          {bookitupDocument && originDocumentId && originalDocumentType && (
            <Text size="xs" color="dimmed" weight="normal">
              Diese <Trans id={`document.${bookitupDocument.documentType.toLowerCase()}`} /> basiert auf einem anderen{' '}
              <Text
                span
                color="blue"
                variant="link"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  const targetLink = eventId
                    ? `/events/${eventId}/documents/${originDocumentId}`
                    : `/documents/${originDocumentId}`;
                  navigate(targetLink);
                }}
              >
                {`${i18n._(`document.${originalDocumentType.toLowerCase()}`)} Nr. ${originalInvoiceNumber}`}
              </Text>
            </Text>
          )}
        </Stack>
        {!smallScaleBusiness && (
          <BookitupSegmentedControl
            value={taxType}
            color="blue"
            data={DataS.netGrossOptions}
            radius="md"
            onChange={(val: TaxType) => savePatch({ taxType: val })}
            sx={{ minWidth: 160 }}
          />
        )}
      </Group>
      {bookitupDocument && (
        <SimpleGrid
          mb="md"
          cols={3}
          spacing="xs"
          breakpoints={[
            { maxWidth: 'md', cols: 2 },
            { maxWidth: 'xs', cols: 1 },
          ]}
        >
          {documentType !== 'REMINDER' && (
            <TextInput
              defaultValue={number}
              placeholder="-"
              label={<Text size="xs">{i18n._('document.no')}</Text>}
              onBlur={(e) => savePatch({ number: e.currentTarget.value })}
              onKeyDown={(e) => {
                if (['\\', '/', '&', '"', "'", '#'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          )}
          <DatePicker
            required
            clearable={false}
            label={
              <Text span size="xs">
                Datum
              </Text>
            }
            placeholder="-"
            defaultValue={date ? new Date(date) : undefined}
            onChange={(val) => val && savePatch({ date: modifyPickedDay(val) })}
          />
          {documentType === 'OFFER' && (
            <DatePicker
              clearable={false}
              label={<Text size="xs">{i18n._('document.validuntil')}</Text>}
              placeholder="-"
              value={validUntil ? new Date(validUntil) : validUntil}
              onChange={(val) => val && savePatch({ validUntil: modifyPickedDay(val) })}
              minDate={date ? new Date(date) : undefined}
            />
          )}
          {documentType && DocumentS.isInvoice(documentType) && (
            <DatePicker
              required
              clearable={false}
              label={
                <Text span size="xs">
                  {i18n._('document.payuntil')}
                </Text>
              }
              placeholder="-"
              value={validUntil ? new Date(validUntil) : validUntil}
              minDate={date ? new Date(date) : undefined}
              onChange={(val) => val && savePatch({ validUntil: modifyPickedDay(val) })}
            />
          )}
          {hasShippingType && (
            <Tooltip disabled={!isReminder} label="Wert kann nur in der ursprünglichen Rechnung geändert werden.">
              <Select
                label={<Text size="xs">Versandart</Text>}
                data={DataS.shippingTypeOptions}
                value={shippingType}
                disabled={isReminder}
                onChange={(val) => val && savePatch({ shippingType: val as ShippingType })}
              />
            </Tooltip>
          )}
          {hasShippingType && shippingType !== 'none' && (
            <Tooltip disabled={!isReminder} label="Wert kann nur in der ursprünglichen Rechnung geändert werden.">
              <DatePicker
                required
                disabled={isReminder}
                label={
                  <Text span size="xs">
                    {hasShippingPeriod ? 'Liefertermin von' : 'Liefertermin'}
                  </Text>
                }
                placeholder="-"
                value={shippingDate ? new Date(shippingDate) : shippingDate}
                onChange={(val) => val && savePatch({ shippingDate: modifyPickedDay(val) })}
              />
            </Tooltip>
          )}
          {hasShippingType && shippingType !== 'none' && hasShippingPeriod && (
            <Tooltip disabled={!isReminder} label="Wert kann nur in der ursprünglichen Rechnung geändert werden.">
              <DatePicker
                disabled={isReminder}
                label={<Text size="xs">Liefertermin bis</Text>}
                placeholder="-"
                value={shippingEndDate ? new Date(shippingEndDate) : shippingEndDate}
                onChange={(val) => val && savePatch({ shippingEndDate: modifyPickedDay(val) })}
              />
            </Tooltip>
          )}
        </SimpleGrid>
      )}
      <Divider my="xl" />
      {isEventScope && bookitupDocument && (
        <EventRichInput
          label="Kopftext"
          value={bookitupDocument.headRow}
          patchField={(val: string) => savePatch({ headRow: val })}
          menu={<DocumentBubbleMenu />}
          styles={TiptapS.TextInputStyles}
        />
      )}
      {!isEventScope && bookitupDocument && (
        <RichInput
          label="Kopftext"
          value={bookitupDocument.headRow}
          patchField={(val: string) => savePatch({ headRow: val })}
          menu={<DocumentBubbleMenu />}
          styles={TiptapS.TextInputStyles}
        />
      )}

      <Space h="xs" />
      <PositionHeader
        documentVersion
        desktopVersion={!usePreviewVersion}
        showDiscount={!isPartialInvoice}
        currencySymbol={currencySymbol}
        isPartialInvouce={documentType === 'PARTIAL_INVOICE'}
        language={bookitupDocument?.language}
        sticky={!isMobile}
      />
      <Stack ref={positionsSectionRef} mt={5} sx={{ scrollMarginTop: 80 }}>
        <PositionGroups usePreviewVersion={usePreviewVersion} products={products} />
        {addingGroupAtIndex !== null && emptyGroups && <Skeleton ml={30} height={56} radius="md" />}
        {loading && <SkeletonItems height={100} />}
        {!loading && emptyPositions && !addingPosition && emptyGroups && addingGroupAtIndex === null && (
          <EmptyPositionsSkeleton />
        )}
        {bookitupDocument &&
          grouplessPositions.map((position) => (
            <DocumentPosition
              key={position.id}
              previewVersion={usePreviewVersion}
              bookitupDocument={bookitupDocument}
              position={position}
              handlers={positionHandlers}
              products={products}
              refreshDocument={refresh}
            />
          ))}
      </Stack>
      {/* Keep stable geometry so native HTML5 drag hit-testing can discover this target before a drag starts. */}
      <div ref={drop} style={{ minHeight: 74 }} aria-label="Positionen ohne Gruppe">
        {canDrop && <DropArea isOver={isOver} />}
      </div>
      {addingPosition && <Skeleton mt="sm" width="100%" height={100} />}
      {!canDrop && !addingPosition && (
        <Group mt="md">
          {!isPartialInvoice && emptyPositions && !addingPosition && !loading && (
            <>
              <LinkText onClick={addPositionHandler}>+ Position hinzufügen</LinkText>
              <LinkText onClick={addProduct}>+ Produkt hinzufügen</LinkText>
            </>
          )}
          {emptyGroups && hasFlowLicence && (
            <LinkText onClick={() => addingGroupAtIndex === null && addGroupHandler()}>+ Gruppe hinzufügen</LinkText>
          )}
        </Group>
      )}
      <Space h="md" />
      {bookitupDocument && <DocumentSum baseDocument={bookitupDocument} savePatch={savePatch} />}
      <DocumentPartialInvoices />
      {documentType === 'INVOICE' && <UnassginedPartialInvoices />}
      <Space h="xl" />
      {isEventScope && bookitupDocument && (
        <EventRichInput
          label="Fußtext"
          value={bookitupDocument.endRow}
          patchField={(val: string) => savePatch({ endRow: val })}
          menu={<DocumentBubbleMenu />}
          styles={{ minHeight: 150 }}
        />
      )}
      {!isEventScope && bookitupDocument && (
        <RichInput
          label="Fußtext"
          value={bookitupDocument.endRow}
          patchField={(val: string) => savePatch({ endRow: val })}
          menu={<DocumentBubbleMenu />}
          styles={{ minHeight: 150 }}
        />
      )}
    </div>
  );
};

export default DocumentSkeleton;
